// @ts-strict-ignore
import React from 'react';

import { StyledAction } from 'flow/editor/styles';
import { ActionType } from 'flow/store/engine/constants';
import type { Action } from 'flow/store/engine/types';
import { getActionName } from 'flow/service/actions';
import PreviewActionTextDropdown from './PreviewActionTextDropdown';
import { StyledFocusedActionText } from './styles';
import { useEditorAccess } from 'flow/editor/context/editorAccessContext';
import { checkIfEditorModeIsEditable } from 'flow/service/general/general';

export default function FocusedActionText({
  focusedAction,
  hasRules,
  hasThen = true,
}: {
  focusedAction: Action;
  hasRules: boolean;
  hasThen?: boolean;
}): JSX.Element | null {
  const { editorMode } = useEditorAccess();
  const inEditMode = checkIfEditorModeIsEditable(editorMode);

  const then = hasThen ? 'Then ' : '';
  const focusedActionName = (
    <StyledAction action={focusedAction}>
      {getActionName(focusedAction)}
    </StyledAction>
  );

  const swappableTagDropdown = (
    <PreviewActionTextDropdown
      action={focusedAction}
      autoFocus
      isPreviewAction
      skipActionTypeSelection
    />
  );

  const tokens = {
    '{{focusedActionName}}': focusedActionName,
    '{{swappableTagDropdown}}': inEditMode ? swappableTagDropdown : focusedActionName,
    '{{previewActionDropdown}}': <PreviewActionTextDropdown />,
  };

  const messages = {
    hasRules: {
      [ActionType.OUTCOME]: `${then}the evaluation will have outcome {{focusedActionName}}`,
      [ActionType.SERVICE]: `${then}service {{focusedActionName}} runs`,
      [ActionType.TAG]: `${then}set tag {{swappableTagDropdown}}`,
      [ActionType.INPUT]: '',
      [ActionType.MATRIX_MODEL]: `${then}run Matrix: {{focusedActionName}}`,
      [ActionType.PREVIEW]: `${then}{{previewActionDropdown}}`,
    },
    noRules: {
      [ActionType.OUTCOME]: `${then}the evaluation will have outcome`,
      [ActionType.SERVICE]: 'Service {{focusedActionName}} always runs',
      [ActionType.TAG]: 'Tag {{swappableTagDropdown}} is always set',
      [ActionType.INPUT]: `{{focusedActionName}} is a ${focusedAction.input?.dataType}-type input attribute`,
      [ActionType.MATRIX_MODEL]: 'Matrix Model {{focusedActionName}} always runs',
      [ActionType.PREVIEW]: `${then}{{previewActionDropdown}}`,
    },
  };

  const parseMessage = (message: string) => {
    const splitMessages = message.split(' ');
    return (
      <StyledFocusedActionText data-testid="focused-action-text">
        {splitMessages.map(word => {
          if (tokens[word]) return <>{tokens[word]}&nbsp;</>;
          return `${word} `;
        })}
      </StyledFocusedActionText>
    );
  };

  const messageBasis = hasRules ? messages.hasRules : messages.noRules;
  const message = messageBasis[focusedAction.type];
  return message ? parseMessage(message) : null;
}
